import { useRouter } from "next/router"
import Link from "next/link"
import NextImg from "next/image"
import { dehydrate, QueryClient } from "react-query"
import HTMLParser from "react-html-parser"
import { twMerge } from "tailwind-merge"
import {
  ArticleContent,
  CityBoard,
  Card,
  ContentTemplate,
  FooterWithDevil,
  Header,
  GuidesCarousel,
  PureAds,
  RandomGuide,
  HeadMeta,
  useSmartBanner,
} from "components"
import {
  getHost,
  getArticleUrl,
  getCityUrl,
  getMainPageUrl,
  LocaleContext,
  DEFAULT_LOCALE,
  removeTags,
} from "lib"
import {
  fetchAllArticlesBySlug,
  fetchArticleBySlug,
  prefetchArticlesByCity,
  prefetchCities,
  prefetchCommonTranslations,
  prefetchPageData,
  useGetArticleBySlug,
  useGetArticlesByCity,
  useGetCities,
  useGetPageData,
  getArticles,
  getSmartBanner,
} from "api"
import {
  purgeArticlePageFiles,
  SMART_BANNER_PATH_VALUES,
  IMAGES_FORMAT_PATH_VALUES,
} from "lib/isr"

import type { GetStaticProps, GetStaticPaths } from "next"
import type { Article, SmartBannerData } from "api"
import type { ImagesFormatPathValues, SmartBannerPathValues } from "lib/isr"

import LipsSvg from "public/images/icons/lips.svg"
import CloudSvg from "public/images/cloud-3.svg"

interface ArticlePageProps {
  url: string
  smartBannerData?: SmartBannerData
  isWebpSupported: boolean
}

type ArticleParsedUrlQuery = {
  slug: string,
  smartBanner: SmartBannerPathValues,
  imagesFormat: ImagesFormatPathValues,
}

export const getStaticProps: GetStaticProps<{}, ArticleParsedUrlQuery> = async(ctx) => {
  const { locale, params } = ctx
  const slug = params?.slug || ""

  const queryClient = new QueryClient()

  const [articles, currentArticle] = await Promise.all([
    fetchAllArticlesBySlug(queryClient, slug?.toString()),
    fetchArticleBySlug(queryClient, slug?.toString(), locale),
  ])

  if (!articles || !articles.length) {
    await purgeArticlePageFiles(slug, locale)

    return {
      redirect: {
        permanent: false,
        destination: "/",
      },
    }
  }

  await Promise.all([
    prefetchPageData(queryClient, "article", locale),
    prefetchCommonTranslations(queryClient, locale),
    prefetchCommonTranslations(queryClient, DEFAULT_LOCALE),
    prefetchCities(queryClient, locale),
  ])

  const smartBannerData = await getSmartBanner(locale)

  if (currentArticle) {
    await prefetchArticlesByCity(queryClient, currentArticle.city.id, locale)
  } else {
    await purgeArticlePageFiles(slug, locale)

    return {
      redirect: {
        permanent: false,
        destination: getMainPageUrl(locale),
      },
    }
  }

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
      url: getHost(locale) + getArticleUrl(slug),
      smartBannerData: params?.smartBanner === "smart-banner-disabled" ? null : smartBannerData,
      isWebpSupported: params?.imagesFormat === "webp",
    },
  }
}

export const getStaticPaths: GetStaticPaths = async () => {
  const allArticles = await getArticles({ locale: "all" })
  const paths: any[] = []

  allArticles.forEach(({ slug, locale, localizations, city }) => {
    if (!city) {
      return
    }

    SMART_BANNER_PATH_VALUES.forEach(smartBanner => {
      IMAGES_FORMAT_PATH_VALUES.forEach(imagesFormat => {
        paths.push({ params: { slug, smartBanner, imagesFormat, }, locale  })
      })
    })
  })

  return {
    paths,
    fallback: true,
  }
}

export default function ArticlePage(props: ArticlePageProps) {
  const { url, smartBannerData, isWebpSupported } = props
  const {
    asPath,
    query: { slug },
  } = useRouter()
  const { data } = useGetPageData("article")
  const { isLoading, data: currentArticle } = useGetArticleBySlug(
    slug as string,
  )
  const { data: articles = [] } = useGetArticlesByCity(currentArticle?.city.id)
  const { data: cities = [] } = useGetCities()
  const smartBanner = useSmartBanner(smartBannerData)

  if (isLoading || !data || !currentArticle) {
    return null
  }

  const cards = articles.reduce((result: Card[], article: Article) => {
    if (article.id !== currentArticle.id) {
      result.push({
        author: article.author,
        city: article.city,
        description: article.title,
        url: getArticleUrl(article.slug),

        className: "group-hover:bg-gray-100",
      })
    }
    return result
  }, [])

  const carouselTitle = (data.locales["other_guides"] || "").replace(
    "%city%",
    currentArticle.city.name_for_carousel || currentArticle.city.name,
  )

  const boardCities = cities.filter(
    (city) => city.slug !== currentArticle.city.slug && city.articles.length,
  )

  const articleTitle = removeTags(currentArticle.title)
  const title = data.locales["tab.title"]
    ? data.locales["tab.title"] + articleTitle
    : articleTitle

  const { main_image: cover } = currentArticle
  const image =
    (isWebpSupported
      ? cover?.formats?.article_cover
      : cover?.formats?.article_cover_original) ?? cover

  return (
    <LocaleContext.Provider value={data}>
      <HeadMeta title={title} meta={currentArticle.meta} url={url} />
      {smartBanner}
      <div className={twMerge(smartBanner && "mt-20 md:mt-[52px]")}>
        <div className="fixed z-10 w-full">
          <Header
            theme="white"
            variant="menu"
            currentCity={currentArticle.city}
          />
        </div>
      </div>
      <div className="bg-white pt-14 text-black md:pt-[82px]">
        <ContentTemplate className="pt-3 pb-6 font-sans lg:pb-8">
          <Link href={getCityUrl(currentArticle.city.slug)}>
            <a className="font-sans-bold text-sm uppercase lg:text-xl lg:leading-normal">
              {currentArticle.city.name}
            </a>
          </Link>
          <h1 className="my-6 break-words font-serif text-4xl uppercase md:text-5xl lg:my-8 lg:text-7xl">
            {HTMLParser(currentArticle.title)}
          </h1>
          {image && (
            <div
              className={twMerge("w-full", `bg-${currentArticle.city.color}`)}
            >
              <NextImg
                key={currentArticle.id}
                src={image.url}
                layout="responsive"
                height={image.height}
                width={image.width}
                alt={cover?.alternativeText || currentArticle.title}
                title={cover?.caption}
                unoptimized
              />
            </div>
          )}
          <div className="mt-6 text-xl leading-8 lg:mt-8 lg:max-w-3xl">
            {HTMLParser(currentArticle.description)}
          </div>
        </ContentTemplate>
      </div>
      <ArticleContent article={currentArticle} />
      <PureAds theme="dark" />

      <div className="relative overflow-hidden bg-white pt-14 pb-31 text-black md:pb-50 md:pt-16 lg:pb-54">
        {cards.length === 0 ? (
          <RandomGuide />
        ) : (
          <>
            <ContentTemplate>
              <h3 className="mb-6 text-3xl uppercase md:text-6xl lg:text-center">
                <LipsSvg className="mb-2 h-8 w-8 md:mb-6 md:h-[52px] md:w-[52px] lg:mx-auto" />
                {HTMLParser(carouselTitle)}
              </h3>
            </ContentTemplate>
            <GuidesCarousel cards={cards} />
          </>
        )}
        <CloudSvg className="absolute -bottom-px -right-36 h-[43px] -scale-x-100 text-gray-400 md:-right-60 md:h-20 lg:left-0" />
      </div>

      <div className="bg-gray-400">
        <ContentTemplate className="pt-14 pb-16 lg:py-20">
          <h3 className="border-b border-gray-250 pb-4 text-xl  md:text-2xl md:leading-7 lg:pb-4 lg:text-3xl">
            {data.locales["city_board.title"]}
          </h3>
          <CityBoard cities={boardCities} />
        </ContentTemplate>
        <FooterWithDevil />
      </div>
    </LocaleContext.Provider>
  )
}
